// @workflow assistant.voice
/**
 * Plays one reply's speech as it arrives (contract "Read aloud"):
 *
 * - With MediaSource for audio/mpeg: the audio element gets the MediaSource at once and play() is
 *   called before anything is awaited, so it stays inside the click (autoplay rules). The chunks
 *   go into the SourceBuffer one at a time; endOfStream() once the body is complete.
 * - Without it: the whole body is read first, then played as a blob.
 * - A refusal, a body that breaks off, an audio error or a play() the browser refuses: FAILED
 *   with its code. stop() aborts, releases, and reports nothing.
 * - Every exit (end, failure, stop) aborts the request, cancels the reader, empties the audio
 *   element and revokes the object URL.
 * - A reply whose whole audio came in is kept in memory (never in browser storage), at most
 *   MAX_KEPT_REPLIES, the oldest dropped first: playing it again makes no request and starts
 *   inside the click, which also lets Try again play where the browser refused a later play().
 *   clear() forgets them all (a new chat, AVA closing for good).
 *
 * Events go to onEvent as reducer actions without a session (the store adds it): PLAYING,
 * PROGRESS { played, duration } (duration only once the whole audio is in), ENDED, FAILED { code }.
 * The audio element, MediaSource and URL are injected, so node --test drives it with fakes.
 * No Vue or path-alias imports.
 */
import { READ_ACTION as A } from '../domain/readAloudState.js';
import { READ_ALOUD_CODE } from '../domain/readAloudCodes.js';

export const AUDIO_MPEG = 'audio/mpeg';
/** How many replies' audio is kept for a second play (about 1 MB each at most). */
export const MAX_KEPT_REPLIES = 10;
const MSE_OPEN = 'open';

const once = (target, ok, bad) => new Promise((resolve, reject) => {
  const done = (fn) => (event) => {
    target.removeEventListener(ok, onOk);
    if (bad) target.removeEventListener(bad, onBad);
    fn(event);
  };
  const onOk = done(resolve);
  const onBad = done(() => reject(new Error(`${bad} on ${ok}`)));
  target.addEventListener(ok, onOk);
  if (bad) target.addEventListener(bad, onBad);
});

/**
 * @param {{
 *   client: { speech: (reply: object) => Promise<Response> },
 *   createAudio: () => HTMLAudioElement,
 *   MediaSourceImpl?: typeof MediaSource|null,
 *   urls: { createObjectURL: Function, revokeObjectURL: Function },
 * }} deps
 */
export function createReadAloudPlayer({ client, createAudio, MediaSourceImpl = null, urls, cacheLimit = MAX_KEPT_REPLIES }) {
  const streams = !!(MediaSourceImpl && typeof MediaSourceImpl.isTypeSupported === 'function' && MediaSourceImpl.isTypeSupported(AUDIO_MPEG));
  /** messageId → the reply's whole audio, oldest first. */
  const kept = new Map();

  function keep(messageId, chunks) {
    kept.delete(messageId);
    kept.set(messageId, new Blob(chunks, { type: AUDIO_MPEG }));
    while (kept.size > cacheLimit) kept.delete(kept.keys().next().value);
    return kept.get(messageId);
  }

  function takeKept(messageId) {
    const blob = kept.get(messageId);
    if (!blob) return null;
    kept.delete(messageId);
    kept.set(messageId, blob); // the newest again
    return blob;
  }

  /**
   * @param {{ conversationId: string, messageId: string, onEvent: (action: object) => void }} reply
   * @returns {{ stop: () => void }}
   */
  function start({ conversationId, messageId, onEvent }) {
    const controller = new AbortController();
    const audio = createAudio();
    let over = false;
    let complete = false;
    let url = null;
    let reader = null;
    let source = null;

    function release() {
      controller.abort();
      if (reader) reader.cancel().catch(() => {}); // already closed or broken: nothing to cancel
      reader = null;
      audio.pause();
      audio.removeAttribute('src');
      audio.load();
      if (url) urls.revokeObjectURL(url);
      url = null;
    }

    function finish(action) {
      if (over) return;
      over = true;
      release();
      onEvent(action);
    }
    const fail = () => finish({ type: A.FAILED, code: READ_ALOUD_CODE.FAILED });

    function stopNow() {
      if (over) return;
      over = true;
      release();
    }

    const duration = () => (complete && Number.isFinite(audio.duration) ? audio.duration : null);
    const progress = () => {
      if (!over) onEvent({ type: A.PROGRESS, played: audio.currentTime, duration: duration() });
    };

    function play() {
      const playing = audio.play();
      if (playing && typeof playing.catch === 'function') playing.catch(() => { if (!over) fail(); });
    }

    audio.addEventListener('playing', () => { if (!over) onEvent({ type: A.PLAYING }); });
    audio.addEventListener('timeupdate', progress);
    audio.addEventListener('ended', () => finish({ type: A.ENDED }));
    audio.addEventListener('error', () => { if (!over) fail(); });

    const known = takeKept(messageId);
    if (known) {
      url = urls.createObjectURL(known);
      complete = true;
      audio.src = url;
      play();
      return { stop: stopNow };
    }

    if (streams) {
      source = new MediaSourceImpl();
      url = urls.createObjectURL(source);
      audio.src = url;
      play();
    }

    async function append(buffer, chunk) {
      const appended = once(buffer, 'updateend', 'error');
      buffer.appendBuffer(chunk);
      await appended;
    }

    async function streamInto(response) {
      if (source.readyState !== MSE_OPEN) await once(source, 'sourceopen');
      if (over) return;
      const buffer = source.addSourceBuffer(AUDIO_MPEG);
      const chunks = [];
      for (;;) {
        const { done, value } = await reader.read();
        if (over) return;
        if (done) break;
        chunks.push(value);
        await append(buffer, value);
        if (over) return;
      }
      if (buffer.updating) await once(buffer, 'updateend');
      if (over) return;
      source.endOfStream();
      keep(messageId, chunks);
      complete = true;
      progress();
    }

    async function downloadThenPlay(response) {
      const chunks = [];
      for (;;) {
        const { done, value } = await reader.read();
        if (over) return;
        if (done) break;
        chunks.push(value);
      }
      url = urls.createObjectURL(keep(messageId, chunks)); // kept first, so a refused play() can be tried again
      complete = true;
      audio.src = url;
      play();
    }

    async function run() {
      let response;
      try {
        response = await client.speech({ conversationId, messageId, signal: controller.signal });
      } catch (err) {
        if (!over) finish({ type: A.FAILED, code: (err && err.code) || READ_ALOUD_CODE.FAILED });
        return;
      }
      if (over) return;
      try {
        reader = response.body.getReader();
        await (streams ? streamInto(response) : downloadThenPlay(response));
      } catch (err) {
        if (!over) fail(); // the body broke off, or the buffer refused a chunk
      }
    }

    run();
    return { stop: stopNow };
  }

  return {
    start,
    /** Forgets every kept reply. */
    clear() {
      kept.clear();
    },
  };
}
